import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import MetaTags from '@/components/meta/MetaTags';
import { useInfiniteRelics } from '@/hooks/use-infinite-relics';
import { LoadingFallback } from '@/components/common/LoadingFallback';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { RelicsErrorBoundary } from '@/components/bulletins/RelicsErrorBoundary';
import BulletinCard from '@/components/bulletins/BulletinCard';
import BulletinsInfiniteLoader from '@/components/bulletins/BulletinsInfiniteLoader';
import { useBulletinGridLayout } from '@/hooks/use-bulletin-grid-layout';

const RelicsTest: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const activeTab: 'pour-toi' | 'updates' = 'pour-toi';

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteRelics(activeTab);

  const relics = useMemo(() => data?.pages.flatMap(p => p.items) ?? [], [data]);
  
  const { gridItems, getColumnClass } = useBulletinGridLayout({
    bulletins: relics,
    squarePercentage: 67,
    horizontalPercentage: 16,
    largePercentage: 17
  });

  const handleLike = async (id: string) => {
    queryClient.setQueryData(['infinite-relics', activeTab], (oldData: any) => {
      if (!oldData?.pages?.length) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map((page: any) => ({
          ...page,
          items: page.items.map((item: any) => item.id === id ? { ...item, like_count: (item.like_count || 0) + 1 } : item)
        }))
      };
    });
  };

  const handleDelete = () => {
    queryClient.invalidateQueries({ queryKey: ['infinite-relics', activeTab], exact: false });
  };

  const handleRelicClick = (id: string) => {
    navigate(`/relics-test/${id}`);
  };

  return (
    <div>
      <MetaTags 
        title="Relics Test – Debug Page"
        description="Full-featured relics page for debugging mobile issues."
        image="/og-image.png"
        url={window.location.href}
      />

      <div className="container max-w-7xl mx-auto pt-25 px-4 py-[22px]">
        <motion.div 
          initial={{ y: -20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }} 
          transition={{ delay: 0.2, duration: 0.3 }} 
          className="mx-0 mb-6"
        >
          <h1 className="text-2xl font-bold text-foreground">Relics Test Page</h1>
          <p className="text-muted-foreground">Full-featured version for debugging</p>
        </motion.div>

        <RelicsErrorBoundary>
          {isLoading && relics.length === 0 ? (
            <div className="flex justify-center my-12">
              <LoadingFallback />
            </div>
          ) : (
            <div className="py-8">
              <div 
                className={`grid ${getColumnClass()} auto-rows-auto min-w-0 max-w-full`}
                style={{ 
                  contain: 'layout',
                  transform: 'translate3d(0,0,0)',
                  willChange: 'transform'
                }}
              >
                {relics.map((relic: any) => {
                  const gridItem = gridItems.find((item) => item.id === relic.id);
                  const spans = gridItem?.spans || {
                    row: 1,
                    col: 1,
                    isHorizontal: false,
                  };

                  return (
                    <div
                      key={relic.id}
                      className={`
                        ${spans.row === 2 ? "row-span-2" : ""} 
                        ${spans.col === 2 ? "col-span-2" : ""} 
                        w-full min-w-0 max-w-full overflow-hidden
                      `}
                      style={{
                        aspectRatio: spans.isHorizontal 
                          ? "2/1" 
                          : spans.row === 2 
                            ? "1/2" 
                            : "1/1",
                        contain: 'layout style'
                      }}
                    >
                      <div className="h-full w-full">
                        <BulletinCard
                          bulletin={relic}
                          onLike={() => handleLike(relic.id)}
                          onDelete={handleDelete}
                          size={
                            spans.isHorizontal
                              ? "horizontal"
                              : spans.row > 1
                                ? "medium"
                                : "small"
                          }
                          isSelected={false}
                          onClose={() => handleRelicClick(relic.id)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <BulletinsInfiniteLoader
                isLoading={isFetchingNextPage}
                hasNextPage={!!hasNextPage}
                onLoadMore={() => fetchNextPage()}
              />
            </div>
          )}
        </RelicsErrorBoundary>
      </div>
    </div>
  );
};

export default RelicsTest;